import React from 'react';
import styles from './AnalyticsBySkuMockPage.module.css';
import { formatValue } from "@/utils/formatUtils";
import { Table, TableColumn } from "@/components/Table/Table";
import Card, { TotalCard } from "@/components/Analytics/Card";
import TextInput from "@/components/forms/TextInput/TextInput";
import IconSearch from "@/components/icons/IconSearch";
import Pagination from "@/components/Pagination/Pagination";
import DateRangeButtons from "@/components/DateRangeSelector/DateRangeButtons";
import { DateRangeValue, startOfToday, endOfToday } from "@/utils/dateUtils";

// Mock data
const mockTimeFrame: DateRangeValue = {
  from: startOfToday.startOf("month").startOf('day').toDate(),
  to: endOfToday.toDate(),
  label: 'This month'
};

const mockOverviewData = {
  highestProfit: {
    sku: 'SKU-001',
    total: 1250.75,
    productName: 'Premium Wireless Headphones',
    image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=100&h=100&fit=crop'
  },
  highestRevenue: {
    sku: 'SKU-002', 
    total: 3450.20,
    productName: 'Smart Fitness Tracker',
    image: 'https://images.unsplash.com/photo-1575311373937-040b8e1fd5b6?w=100&h=100&fit=crop'
  },
  highestUnitsSold: {
    sku: 'SKU-003',
    total: 147,
    productName: 'Bluetooth Speaker',
    image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=100&h=100&fit=crop'
  },
  highestRefunds: {
    sku: 'SKU-004',
    total: 8,
    productName: 'USB-C Cable',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop'
  },
  totalSKUs: 24
};

const mockProductData = [
  {
    sku: 'SKU-001',
    name: 'Premium Wireless Headphones',
    image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=60&h=60&fit=crop',
    listPrice: 199.99,
    avgBuyCost: 85.50,
    inStockQty: 45,
    revenue: 2399.88,
    profit: 1250.75,
    unitsSold: 12,
    refunds: 1
  },
  {
    sku: 'SKU-002',
    name: 'Smart Fitness Tracker',
    image: 'https://images.unsplash.com/photo-1575311373937-040b8e1fd5b6?w=60&h=60&fit=crop',
    listPrice: 149.99,
    avgBuyCost: 68.00,
    inStockQty: 32,
    revenue: 3450.20,
    profit: 1876.77,
    unitsSold: 23,
    refunds: 2
  },
  {
    sku: 'SKU-003',
    name: 'Bluetooth Speaker',
    image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=60&h=60&fit=crop',
    listPrice: 79.99,
    avgBuyCost: 32.50,
    inStockQty: 89,
    revenue: 11758.53,
    profit: 6987.03,
    unitsSold: 147,
    refunds: 5
  },
  {
    sku: 'SKU-004',
    name: 'USB-C Cable',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=60&h=60&fit=crop',
    listPrice: 24.99,
    avgBuyCost: 8.75,
    inStockQty: 156,
    revenue: 1874.25,
    profit: 1217.00,
    unitsSold: 75,
    refunds: 8
  },
  {
    sku: 'SKU-005',
    name: 'Wireless Charging Pad',
    image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=60&h=60&fit=crop',
    listPrice: 49.99,
    avgBuyCost: 22.00,
    inStockQty: 67,
    revenue: 1349.73,
    profit: 754.72,
    unitsSold: 27,
    refunds: 3
  },
  {
    sku: 'SKU-006',
    name: 'Smartphone Case',
    image: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=60&h=60&fit=crop',
    listPrice: 29.99,
    avgBuyCost: 12.50,
    inStockQty: 124,
    revenue: 2099.32,
    profit: 1224.83,
    unitsSold: 70,
    refunds: 4
  },
  {
    sku: 'SKU-007',
    name: 'Gaming Mouse',
    image: 'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=60&h=60&fit=crop',
    listPrice: 89.99,
    avgBuyCost: 45.00,
    inStockQty: 58,
    revenue: 3149.65,
    profit: 1574.65,
    unitsSold: 35,
    refunds: 2
  },
  {
    sku: 'SKU-008',
    name: 'LED Desk Lamp',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop',
    listPrice: 64.99,
    avgBuyCost: 28.75,
    inStockQty: 73,
    revenue: 1947.20,
    profit: 1087.32,
    unitsSold: 30,
    refunds: 1
  },
];

const AnalyticsBySkuMockPage = () => {
  const cardAnalytics = [
    {
      sku: mockOverviewData.highestProfit.sku,
      label: "Highest Profit",
      value: formatValue(mockOverviewData.highestProfit.total, "currency"),
      name: mockOverviewData.highestProfit.productName,
      image: mockOverviewData.highestProfit.image,
    },
    {
      sku: mockOverviewData.highestRevenue.sku,
      label: "Highest Revenue", 
      value: formatValue(mockOverviewData.highestRevenue.total, "currency"),
      name: mockOverviewData.highestRevenue.productName,
      image: mockOverviewData.highestRevenue.image,
    },
    {
      sku: mockOverviewData.highestUnitsSold.sku,
      label: "Highest Units Sold",
      value: formatValue(mockOverviewData.highestUnitsSold.total, "number"),
      name: mockOverviewData.highestUnitsSold.productName,
      image: mockOverviewData.highestUnitsSold.image,
    },
    {
      sku: mockOverviewData.highestRefunds.sku,
      label: "Most Refunds",
      value: formatValue(mockOverviewData.highestRefunds.total, "number"),
      name: mockOverviewData.highestRefunds.productName,
      image: mockOverviewData.highestRefunds.image,
    },
    {
      label: "Amount of SKUs",
      value: formatValue(mockOverviewData.totalSKUs, "number"),
      subHeading: "Total",
    }
  ];

  const columns: TableColumn<any>[] = [
    {
      title: 'Image',
      id: 'image',
      dataKey: 'image',
      sortable: false,
      pinned: 'left',
      width: '4rem',
      renderRowCell: (row) => (
        <div className={styles.imageButton}>
          <img src={row.getCellData('image')} alt={row.getCellData('name')} className={styles.image}/>
        </div>
      ),
    },
    {
      title: 'Product',
      id: 'name',
      dataKey: 'name',
      sortable: true,
      width: '19rem',
      renderRowCell: (row) => (
        <div className={styles.productCell}>
          <span>{row.getCellData('name')}</span>
        </div>
      ),
    },
    {
      title: 'SKU',
      id: 'sku',
      dataKey: 'sku',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{row.getCellData('sku')}</span>
        </div>
      ),
    },
    {
      title: 'List Price',
      id: 'listPrice',
      dataKey: 'listPrice',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{formatValue(row.getCellData('listPrice'), 'currency')}</span>
        </div>
      )
    },
    {
      title: 'Buy Cost',
      id: 'avgBuyCost',
      dataKey: 'avgBuyCost',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{formatValue(row.getCellData('avgBuyCost'), "currency")}</span>
        </div>
      )
    },
    {
      title: 'In Stock Qty',
      id: 'inStockQty',
      dataKey: 'inStockQty',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{row.getCellData('inStockQty')}</span>
        </div>
      )
    },
    {
      title: 'Revenue',
      id: 'revenue',
      dataKey: 'revenue',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{formatValue(row.getCellData('revenue'), 'currency')}</span>
        </div>
      )
    },
    {
      title: 'Profit',
      id: 'profit',
      dataKey: 'profit',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{formatValue(row.getCellData('profit'), 'currency')}</span>
        </div>
      )
    },
    {
      title: 'Units Sold',
      id: 'unitsSold',
      dataKey: 'unitsSold',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{row.getCellData('unitsSold')}</span>
        </div>
      )
    },
    {
      title: 'Refunds',
      id: 'refunds',
      dataKey: 'refunds',
      width: '7.5rem',
      sortable: true,
      renderRowCell: (row) => (
        <div className={styles.rightAlign}>
          <span>{row.getCellData('refunds')}</span>
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>SKU Profitability</h1>
        <DateRangeButtons
          timeFrame={mockTimeFrame}
          setTimeFrame={() => {}} // disabled for mock
        />
      </div>

      <div className={styles.cards}>
        {cardAnalytics.map((card, index) => (
          <div key={index} className={styles.cardHeight}>
            {card.image ? (
              <Card
                label={card.label}
                productName={card.name}
                value={card.value}
                isLoading={false}
                imageUrl={card.image}
              />
            ) : (
              <TotalCard
                label={card.label}
                value={card.value}
                isLoading={false}
                subHeading={card.subHeading}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.tableContainer}>
        <Table
          tableName={'mock-profit-by-sku-table'}
          showSort={false}
          data={mockProductData}
          columns={columns}
          isLoading={false}
          header={
            <TextInput
              className={styles.searchInput}
              placeholder={"Product Name, SKU, or ASIN"}
              prefix={<IconSearch/>}
              value={''}
              onChange={() => {}} // disabled for mock
            />
          }
          footer={
            <Pagination
              total={mockProductData.length}
              pageSize={10}
              onChangePageSize={() => {}} // disabled for mock
              page={1}
              onChangePage={() => {}} // disabled for mock
              showPageSelector={false}
              pageSizeOptions={[5, 10, 20]}
            />
          }
          onSort={() => {}} // disabled for mock
        />
      </div>
    </div>
  );
};

export default AnalyticsBySkuMockPage;